import { StationAvatar } from '@app/components/base/stations/StationAvatar';
import {
  StationIndicators,
  StationIndicatorsVariant,
} from '@app/components/stations/StationIndicators';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MinimumStation, Station } from '@app/core/types/stations';

type StationLabelSize = 'md' | 'sm';

interface Props {
  station: Station | MinimumStation;
  noTypo?: boolean;
  /** When true, the station name uses the theme bold weight (e.g. row hover in dense lists). */
  emphasized?: boolean;
  /**
   * Layout density: `md` uses a 24px avatar and 0.875rem name; `sm` uses 20px and 0.75rem.
   */
  size?: StationLabelSize;
  /**
   * Keeps the name on one line with an ellipsis when space is tight (e.g. flex layouts, foreignObject).
   * Requires a bounded width on an ancestor; use with `minWidth: 0` on flex parents.
   */
  singleLineEllipsis?: boolean;
  /**
   * Controls if and how station indicators are rendered.
   */
  indicatorsDisplay?: boolean | StationIndicatorsVariant;
}

const AVATAR_SIZE: Record<StationLabelSize, number> = {
  md: 24,
  sm: 20,
};

const NAME_FONT_SIZE: Record<StationLabelSize, string> = {
  md: '0.875rem',
  sm: '0.75rem',
};

/**
 * Renders a station avatar next to its name, with optional compact sizing for dense UIs.
 */
export function StationLabel(props: Props) {
  const {
    station,
    noTypo = true,
    emphasized = false,
    size = 'md',
    singleLineEllipsis = false,
    indicatorsDisplay = 'icon',
  } = props;

  const theme = useTheme();
  const avatarSize = AVATAR_SIZE[size];
  const nameFontSize = NAME_FONT_SIZE[size];

  /** Numeric weights so `font-weight` can transition smoothly when `emphasized` changes. */
  const nameFontWeight = emphasized
    ? theme.typography.fontWeightBold
    : theme.typography.fontWeightRegular;

  const nameFontWeightSx = {
    fontWeight: nameFontWeight,
    transition: 'font-weight 200ms ease-in-out',
  };

  const nameEllipsisSx = singleLineEllipsis
    ? {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }
    : undefined;

  const nameTextSx = { fontSize: nameFontSize, ...nameFontWeightSx, ...nameEllipsisSx };
  const stationIndicatorsVariant: StationIndicatorsVariant | null =
    indicatorsDisplay === false ? null : indicatorsDisplay === true ? 'icon' : indicatorsDisplay;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={singleLineEllipsis ? { minWidth: 0, maxWidth: '100%' } : undefined}
    >
      <StationAvatar
        stationId={station.id}
        size={avatarSize}
        sx={singleLineEllipsis ? { flexShrink: 0 } : undefined}
      />
      {!noTypo && (
        <Typography variant="body2" sx={nameTextSx}>
          {station.name}
        </Typography>
      )}
      {noTypo &&
        (singleLineEllipsis ? (
          <Box sx={nameTextSx}>{station.name}</Box>
        ) : (
          <Box
            component="span"
            sx={{
              ...(size === 'sm' ? { fontSize: nameFontSize } : {}),
              ...nameFontWeightSx,
            }}
          >
            {station.name}
          </Box>
        ))}
      {stationIndicatorsVariant && (
        <StationIndicators
          station={station}
          variant={stationIndicatorsVariant}
          size={size === 'sm' ? 'small' : 'medium'}
        />
      )}
    </Box>
  );
}
